import * as ROUTES from '../common/routes';
import * as C from '../common/constants';
import React, { useEffect, useState } from 'react';
import AlbumBackground from './AlbumBackground';
import { getText, getPath } from '../common/utils/helpers';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchData, showSuccess } from '../common/app/actions';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import backImg from '../assets/img/img_return.png';
import { useWindowDimensions } from '../hooks';
import Snackbar from '@material-ui/core/Snackbar';
import clsx from 'clsx';
import {
  StyleRules,
  Theme,
  createStyles,
  makeStyles,
} from '@material-ui/core/styles';

interface Props {
  children: React.ReactNode;
}

const useStyles = makeStyles(
  (theme: Theme): StyleRules =>
    createStyles({
      layoutContainerStyle: {
        position: 'absolute',
        width: '100%',
        height: 'auto',
        minHeight: '100%',
        left: 0,
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      },
      contentContainerStyle: {
        position: 'relative',
        width: '100%',
        maxWidth: `${C.MAX_WIDTH}px`,
        display: 'flex',
        flexDirection: 'column',
        padding: '0 50px',
      },
      headerContainerStyle: {
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '30px 0',
      },
      titleContainerStyle: {
        position: 'relative',
        whiteSpace: 'nowrap',
        fontWeight: FONT_WEIGHT.bold,
        fontSize: '32px',
        fontFamily: FONTS.source,
        color: COLORS.appBrown,
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: '0.7',
            transition: '0.5s ease',
          },
        },
      },
      textContainerStyle: {
        position: 'relative',
        width: '100%',
        whiteSpace: 'normal',
        fontSize: '18px',
        fontFamily: FONTS.roboto,
        color: COLORS.appText,
      },
      backgroundWrapperStyle: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        overflow: 'hidden',
      },
      layoutWrapperStyle: {
        position: 'relative',
        width: '100%',
        height: '100%',
        maxWidth: `${C.MAX_WIDTH}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px 50px',
        boxSizing: 'border-box',
      },
      backStyle: {
        position: 'relative',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: '0.7',
            transition: '0.5s ease',
          },
        },
      },
      disabledStyle: {
        pointerEvents: 'none',
      },
      snackbarStyle: {
        background: COLORS.pastelGreen,
      },
      [`@media (max-width: 800px)`]: {
        layoutWrapperStyle: {
          padding: '16px 30px',
        },
        contentContainerStyle: {
          padding: '0 30px',
        },
        headerContainerStyle: {
          padding: '20px 0',
        },
      },
      [`@media (max-width: 600px)`]: {
        layoutWrapperStyle: {
          padding: '16px 20px',
        },
        contentContainerStyle: {
          padding: '0 20px',
        },
        headerContainerStyle: {
          padding: '10px 0',
        },
      },
    })
);

const Layout = ({ children }: Props): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const homeData = useSelector((state) => state.app.homeData);
  const successData = useSelector((state) => state.app.successData);
  const lng = useSelector((state) => state.app.lng);
  const history = useHistory();
  const location = useLocation();

  const { scrollHeight } = useWindowDimensions();

  const title: string = getText(homeData?.content, 'title', lng);
  const rawText: string = getText(homeData?.content, 'text', lng);
  const text: string = rawText.replace(/(?:\r\n|\r|\n)/g, '<br>');

  useEffect(() => {
    dispatch(fetchData(C.HOME_DATA, { home: true }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    history.push(ROUTES.ROOT);
  };

  const onSnackbarClose = () => {
    dispatch(showSuccess());
  };

  const isRoot: boolean = location.pathname === ROUTES.ROOT;

  return (
    <div
      className={classes.layoutContainerStyle}
      style={{ height: `${scrollHeight}px` }}
    >
      <div className={classes.backgroundWrapperStyle}>
        <AlbumBackground />
      </div>
      <div className={classes.contentContainerStyle}>
        <div className={classes.headerContainerStyle}>
          <div
            className={clsx(
              classes.titleContainerStyle,
              isRoot && classes.disabledStyle
            )}
            onClick={onClick}
          >
            {title}
          </div>
          {!isRoot && (
            <img
              className={classes.backStyle}
              src={backImg}
              alt=""
              onClick={onClick}
            />
          )}
        </div>
        <div
          className={classes.textContainerStyle}
          dangerouslySetInnerHTML={{
            __html: text,
          }}
        />
      </div>
      <div className={classes.layoutWrapperStyle}>{children}</div>
      {successData && (
        <Snackbar
          ContentProps={{ className: classes.snackbarStyle }}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={Boolean(successData)}
          onClose={onSnackbarClose}
          message={successData}
        />
      )}
    </div>
  );
};

export default Layout;
